import { lazy, Suspense } from 'react';
import NotFound from '../pages/NotFound';

// Every lazy route renders inside this fallback while its chunk downloads.
// Without it, a route that loads slowly (large chunk, slow connection) leaves
// the whole app blank for the duration of the download — the "blank page"
// reports on client-side navigation.
const RouteFallback = (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-orange" />
  </div>
);

const withSuspense = (element) => <Suspense fallback={RouteFallback}>{element}</Suspense>;

const TestimonialsPage = lazy(() => import('../pages/testimonials/page'));

const HomePage = lazy(() => import('../pages/home/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const ServicesPage = lazy(() => import('../pages/services/page'));
const ServiceDetailPage = lazy(() => import('../pages/service-detail/page'));
const InsightsPage = lazy(() => import('../pages/insights/page'));
const InsightDetailPage = lazy(() => import('../pages/insight-detail/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const PurchasePage = lazy(() => import('../pages/purchase/page'));
const DashboardLayout = lazy(() => import('../pages/dashboard/page'));
const PrivacyPolicyPage = lazy(() => import('../pages/privacy-policy/page'));
const DashboardOverview = lazy(() => import('../pages/dashboard/overview/page'));
const InvestmentPlan = lazy(() => import('../pages/dashboard/investment-plan/page'));
const Performance = lazy(() => import('../pages/dashboard/performance/page'));
const Profile = lazy(() => import('../pages/dashboard/profile/page'));
const AdminManage = lazy(() => import('../pages/dashboard/admin-manage/page'));
const Testimonials = lazy(() => import('../pages/dashboard/testimonials/page'));
const Webinars = lazy(() => import('../pages/dashboard/webinars/page'));
const PressRelease = lazy(() => import('../pages/dashboard/press-release/page'));
const Faqs = lazy(() => import('../pages/dashboard/faqs/page'));

const routes = [
  {
    path: '/',
    element: withSuspense(<HomePage />),
  },
  {
    path: '/about',
    element: withSuspense(<AboutPage />),
  },
  {
    path: '/services',
    element: withSuspense(<ServicesPage />),
  },
  {
    path: '/services/:serviceId',
    element: withSuspense(<ServiceDetailPage />),
  },
  {
    path: '/insights',
    element: withSuspense(<InsightsPage />),
  },
  {
    path: '/insights/:insightId',
    element: withSuspense(<InsightDetailPage />),
  },
  {
    path: '/contact',
    element: withSuspense(<ContactPage />),
  },
  {
    path: '/purchase/:serviceId',
    element: withSuspense(<PurchasePage />),
  },
  {
    path: '/testimonials',
    element: withSuspense(<TestimonialsPage />),
  },
  {
    path: '/dashboard',
    element: withSuspense(<DashboardLayout />),
    children: [
      {
        index: true,
        element: withSuspense(<DashboardOverview />)
      },
      {
        path: 'investment-plan',
        element: withSuspense(<InvestmentPlan />)
      },
      {
        path: 'performance',
        element: withSuspense(<Performance />)
      },
      {
        path: 'profile',
        element: withSuspense(<Profile />)
      },
      {
        path: 'admin-manage',
        element: withSuspense(<AdminManage />)
      },
      {
        path: 'testimonials',
        element: withSuspense(<Testimonials />)
      },
      {
        path: 'webinars',
        element: withSuspense(<Webinars />)
      },
      {
        path: 'press-release',
        element: withSuspense(<PressRelease />)
      },
      {
        path: 'faqs',
        element: withSuspense(<Faqs />)
      }
    ]
  },
  {
    path: '/privacy-policy',
    element: withSuspense(<PrivacyPolicyPage />),
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;